import React, { useState, useEffect } from 'react';
import Modal from '../components/Modal';
import authFetch from '../utils/api';
import './Users.css';

const ROLES = ['admin', 'manager', 'tech'];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'tech'
  });

  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await authFetch('/api/users');
      if (res.ok) {
        setUsers(await res.json());
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await authFetch('/api/users', {
        method: 'POST',
        body: JSON.stringify(formData)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Failed to create user');
        return;
      }
      setSuccess(`Account created for ${data.username}`);
      setShowModal(false);
      resetForm();
      fetchUsers();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Something went wrong');
    }
  };

  const handleToggleActive = async (user) => {
    const newStatus = user.is_active ? 0 : 1;
    try {
      const res = await authFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: newStatus })
      });
      if (res.ok) {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error updating user:', err);
    }
  };

  const handleRoleChange = async (user, newRole) => {
    try {
      const res = await authFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error updating role:', err);
    }
  };

  const handleToggleFinancials = async (user) => {
    try {
      const res = await authFetch(`/api/users/${user.id}`, {
        method: 'PUT',
        body: JSON.stringify({ can_view_financials: user.can_view_financials ? 0 : 1 })
      });
      if (res.ok) {
        fetchUsers();
      }
    } catch (err) {
      console.error('Error updating financials access:', err);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    try {
      const res = await authFetch(`/api/users/${selectedUser.id}/reset-password`, {
        method: 'POST',
        body: JSON.stringify({ password: resetPassword })
      });
      const data = await res.json();
      if (!res.ok) {
        setResetError(data.error || 'Failed to reset password');
        return;
      }
      setSuccess(`Password reset for ${selectedUser.username}`);
      setShowResetModal(false);
      setResetPassword('');
      setSelectedUser(null);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setResetError('Something went wrong');
    }
  };

  const resetForm = () => {
    setFormData({ username: '', email: '', password: '', role: 'tech' });
    setError('');
  };

  const roleBadgeClass = (role) => {
    if (role === 'admin') return 'users__role-badge users__role-badge--admin';
    if (role === 'manager') return 'users__role-badge users__role-badge--manager';
    return 'users__role-badge users__role-badge--tech';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <h1 className="page-header__title">Users</h1>
        <div className="page-header__actions">
          <button className="btn btn--primary" onClick={() => { resetForm(); setShowModal(true); }}>
            + Add User
          </button>
        </div>
      </div>

      {success && (
        <div className="users__success-banner">{success}</div>
      )}

      <div className="users__table-wrap">
        <table className="users__table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Financials Access</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} className={!user.is_active ? 'users__row--inactive' : ''}>
                <td>
                  <div className="users__user-cell">
                    <div className="users__avatar">{user.username.charAt(0).toUpperCase()}</div>
                    <span className="users__username">{user.username}</span>
                  </div>
                </td>
                <td className="users__email">{user.email}</td>
                <td>
                  <select
                    className="users__role-select"
                    value={user.role}
                    onChange={(e) => handleRoleChange(user, e.target.value)}
                  >
                    {ROLES.map(r => (
                      <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <span className={`users__status ${user.is_active ? 'users__status--active' : 'users__status--inactive'}`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {user.role === 'admin' ? (
                    <span className="users__financials-na">&mdash;</span>
                  ) : (
                    <button
                      className={`btn btn--small ${user.can_view_financials ? 'btn--danger' : 'btn--outline'}`}
                      onClick={() => handleToggleFinancials(user)}
                    >
                      {user.can_view_financials ? 'Revoke' : 'Grant'}
                    </button>
                  )}
                </td>
                <td>
                  <div className="users__actions">
                    <button
                      className="btn btn--small btn--secondary"
                      onClick={() => { setSelectedUser(user); setResetPassword(''); setResetError(''); setShowResetModal(true); }}
                    >
                      Reset PW
                    </button>
                    <button
                      className={`btn btn--small ${user.is_active ? 'btn--danger' : 'btn--outline'}`}
                      onClick={() => handleToggleActive(user)}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      <Modal isOpen={showModal} onClose={() => { setShowModal(false); resetForm(); }} title="Add User">
        <form onSubmit={handleSubmit}>
          {error && <div className="users__form-error">{error}</div>}

          <div className="form-group">
            <label className="form-label">Username *</label>
            <input
              type="text"
              className="form-input"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              placeholder="e.g., john_tech"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Email *</label>
            <input
              type="email"
              className="form-input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="john@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Password *</label>
            <input
              type="password"
              className="form-input"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="Min. 6 characters"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Role *</label>
            <select
              className="form-select"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
            >
              {ROLES.map(r => (
                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
              ))}
            </select>
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">Create User</button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={showResetModal}
        onClose={() => { setShowResetModal(false); setResetPassword(''); setResetError(''); }}
        title={`Reset Password — ${selectedUser?.username}`}
      >
        <form onSubmit={handleResetPassword}>
          {resetError && <div className="users__form-error">{resetError}</div>}

          <div className="form-group">
            <label className="form-label">New Password *</label>
            <input
              type="password"
              className="form-input"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              placeholder="Min. 6 characters"
              required
            />
          </div>

          <div className="modal__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => { setShowResetModal(false); setResetPassword(''); setResetError(''); }}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn--primary">Reset Password</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Users;
